import { Loader2, Mail, Trash2 } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { useDeleteMailConnection, useMailConnections, useMailProviders } from '@/hooks/useMail'
import { usePermission } from '@/hooks/usePermission'
import { startMailOAuth } from '@/services/api/mail.service'
import { PERMISSION_KEYS } from '@/stores/permission.store'
import type { MailConnection, MailProvider, MailScope } from '@/types/api/mail'
import { getErrorMessage } from '@/utils/error-parser'

import { ImapConnectDialog } from './ImapConnectDialog'

const PROVIDER_LABEL: Record<MailProvider, string> = { GMAIL: 'Gmail', OUTLOOK: 'Outlook', IMAP: 'Other (IMAP)' }

/** Mailboxes the app may read and send from: the user's own, and the workspace's shared ones. */
export function MailboxesPage() {
  const { can } = usePermission()
  const { data: connections, isLoading } = useMailConnections()
  const mine = connections?.filter((c) => c.scope === 'USER') ?? []
  const shared = connections?.filter((c) => c.scope === 'WORKSPACE') ?? []

  return (
    <div className="mx-auto max-w-4xl space-y-6">
      <div>
        <h1 className="text-3xl font-bold">Mailboxes</h1>
        <p className="mt-1 text-neutral-500">
          Connect a mailbox to link emails to records and send replies from automations.
        </p>
      </div>
      <MailboxSection
        title="My mailboxes"
        description="Only you can search these. Automations can send as you when you trigger them."
        scope="USER"
        connections={mine}
        isLoading={isLoading}
        canConnect
      />
      {can(PERMISSION_KEYS.MAILBOX_USE_WORKSPACE) && (
        <MailboxSection
          title="Workspace mailboxes"
          description="Shared mailboxes, such as support@. Anyone allowed to use them can search them."
          scope="WORKSPACE"
          connections={shared}
          isLoading={isLoading}
          canConnect={can(PERMISSION_KEYS.MAILBOX_MANAGE)}
        />
      )}
    </div>
  )
}

interface SectionProps {
  title: string
  description: string
  scope: MailScope
  connections: MailConnection[]
  isLoading: boolean
  canConnect: boolean
}

function MailboxSection({ title, description, scope, connections, isLoading, canConnect }: SectionProps) {
  const { data: providers } = useMailProviders()
  const [starting, setStarting] = useState<MailProvider | null>(null)
  const [imapOpen, setImapOpen] = useState(false)
  const remove = useDeleteMailConnection()

  async function connect(provider: MailProvider) {
    setStarting(provider)
    try {
      window.location.href = await startMailOAuth(provider, scope)
    } catch (error) {
      toast.error(getErrorMessage(error, 'Could not start the sign-in'))
      setStarting(null)
    }
  }

  const options = (['GMAIL', 'OUTLOOK'] as const).filter((p) => (p === 'GMAIL' ? providers?.gmail : providers?.outlook))

  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {isLoading ? (
          <Skeleton className="h-16 w-full" />
        ) : connections.length === 0 ? (
          <p className="text-sm text-neutral-500">No mailbox connected yet.</p>
        ) : (
          <ul className="divide-y rounded-lg border">
            {connections.map((c) => (
              <li key={c.id} className="flex items-center gap-3 p-3">
                <Mail className="h-4 w-4 shrink-0 text-neutral-500" />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium">{c.emailAddress}</p>
                  <p className="text-xs text-neutral-500">{PROVIDER_LABEL[c.provider]}</p>
                </div>
                {c.status === 'NEEDS_REAUTH' && <Badge variant="destructive">Reconnect needed</Badge>}
                {(scope === 'USER' || canConnect) && (
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label={`Remove ${c.emailAddress}`}
                    disabled={remove.isPending}
                    onClick={() =>
                      remove.mutate(c.id, {
                        onSuccess: () => toast.success('Mailbox removed'),
                        onError: (error) => toast.error(getErrorMessage(error, 'Could not remove the mailbox')),
                      })
                    }
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
              </li>
            ))}
          </ul>
        )}
        {canConnect && (
          <div className="flex flex-wrap gap-2">
            {options.map((provider) => (
              <Button key={provider} variant="outline" disabled={starting !== null} onClick={() => connect(provider)}>
                {starting === provider && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Connect {PROVIDER_LABEL[provider]}
              </Button>
            ))}
            <Button variant="outline" onClick={() => setImapOpen(true)}>Connect other (IMAP)</Button>
          </div>
        )}
      </CardContent>
      {imapOpen && <ImapConnectDialog scope={scope} onClose={() => setImapOpen(false)} />}
    </Card>
  )
}
